import React, { ForwardedRef, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BaseSelectBoxProps, SelectOptionType, Size } from '@/components/content/SelectBoxTypes'
import styled, { css, CSSObject } from 'styled-components'
import { colors, colorSystem, fontSystem, zIndexSystem } from '@/styles/theme'
import { HStack, VStack, Icon, Checkbox } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'
import { isArray, isEmpty, keyBy, size as lodashSize, toString } from 'lodash-es'
import { createPortal } from 'react-dom'
import useClickOutside from '@/hooks/useClickOutside'

export interface MultipleSelectBoxProps<T = string | number | boolean> extends BaseSelectBoxProps<T> {
  showSelectAll?: boolean
  values?: T[]
  onChange?: (v: T[]) => void
  maxHeight?: number
}

export type MultipleSelectBoxWithoutOption<T> = Omit<MultipleSelectBoxProps<T>, 'options'>

function MultipleSelectBox<T = string | number | boolean>(props: MultipleSelectBoxProps<T>, selectRef: ForwardedRef<HTMLDivElement>) {
  const {
    portalId = 'root',
    className,
    showSelectAll = true,
    options,
    values,
    disabled = false,
    readonly = false,
    placeholder = '옵션을 선택해 주세요.',
    onChange,
    size = 'md',
    position = 'bottom',
    maxHeight = 223,
    parentRef,
  } = props

  const $checkbox = useRef<HTMLInputElement>(null)
  const $ref = useRef<HTMLDivElement>(null)
  const $select = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if ($select.current && selectRef) {
      ;(selectRef as MutableRefObject<HTMLDivElement>).current = $select.current
    }
  }, [$select])

  const selectedOptMap = useMemo<Record<string, T>>(() => keyBy(values, (v) => toString(v)), [values])
  const optionMap = useMemo<Record<string, SelectOptionType<T>>>(() => keyBy(options, (opt) => toString(opt.value)), [options])

  const isAllSelected = useMemo(() => lodashSize(selectedOptMap) === lodashSize(optionMap), [selectedOptMap, optionMap])

  const [isOpen, setIsOpen] = useState(false)
  const [openedStyle, setOpenedStyle] = useState<CSSObject>({})

  const handleOpen = useCallback(() => {
    if (!options.length || disabled || readonly) {
      return
    }

    setIsOpen(true)
  }, [options?.length, selectRef, size, disabled, readonly])

  const handleClose = useCallback(
    (e?: Event) => {
      if (!e) return

      const $target = e.target as HTMLElement
      if (!$checkbox.current?.contains($target) && !$ref.current?.contains($target)) {
        setIsOpen(false)
      }
    },
    [$checkbox, $ref],
  )

  useClickOutside($ref, handleClose)

  const onClickOption = useCallback(
    (checked: boolean, v: SelectOptionType<T>['value']) => {
      if (!onChange) return

      if (checked) {
        onChange([...(values ?? []), v])
      } else {
        onChange((values ?? []).filter((val) => val !== v))
      }
    },
    [onChange, values],
  )

  const onSetOpenedStyle = useCallback(() => {
    if (!portalId || !$select.current) return

    const { top, left, width, bottom } = $select.current.getBoundingClientRect()
    if (position === 'top') {
      setOpenedStyle({
        bottom: `${bottom + (size === 'md' ? 32 : 26)}px`,
        left: `${left}px`,
        width: `${width}px`,
        right: 'unset',
      })
    } else {
      setOpenedStyle({
        top: `${top + (size === 'md' ? 32 : 26)}px`,
        left: `${left}px`,
        width: `${width}px`,
        right: 'unset',
      })
    }
  }, [portalId])

  useEffect(() => {
    if (portalId) {
      setTimeout(() => {
        onSetOpenedStyle()
      }, 0)
    }
  }, [portalId, onSetOpenedStyle])

  useEffect(() => {
    const onResize = () => {
      setIsOpen(false)
      onSetOpenedStyle()
    }

    window.addEventListener('resize', onResize)
    if (parentRef?.current) parentRef.current.addEventListener('scroll', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      if (parentRef?.current) parentRef.current.removeEventListener('scroll', onResize)
    }
  }, [handleClose, onSetOpenedStyle])

  const optionDom = (
    <SelectBoxOptions $size={size} $maxHeight={maxHeight} ref={$checkbox} style={openedStyle} gap={8} align="flex-start">
      {showSelectAll && (
        <Option
          block
          size="md"
          $border
          labelText="전체선택"
          checked={isAllSelected}
          onChange={() => onChange && onChange(isAllSelected ? [] : options.map((opt) => opt.value))}
        />
      )}

      {options.map((option) => (
        <Option
          block
          size="md"
          checked={!!selectedOptMap[`${option.value}`]}
          labelText={`${option.label}`}
          onChange={(checked: boolean) => onClickOption(checked, option.value)}
          key={`${option.key || option.value}-${option.value}`}
        />
      ))}
    </SelectBoxOptions>
  )

  return (
    <Wrapper ref={$ref} className={className}>
      <SelectBoxWrap
        ref={$select}
        disabled={disabled || readonly}
        align="center"
        justify="space-between"
        onClick={handleOpen}
        $isOpen={isOpen}
        $size={size}
      >
        <Value kind="Body_14_regular" color={colorSystem.text.common[disabled ? 'disabled' : !isEmpty(values) ? 'primary' : 'tertiary']}>
          {isArray(values) && values?.length && lodashSize(optionMap)
            ? values?.map((val) => optionMap[`${val}`]?.label || '').join(', ')
            : placeholder}
        </Value>
        {!readonly && <Icon name={`arrow-${isOpen ? 'up' : 'down'}-filled`} size={16} color={'gray500'} />}
      </SelectBoxWrap>

      {isOpen && !disabled && !readonly
        ? portalId
          ? createPortal(optionDom, document.getElementById(portalId) ?? document.body)
          : optionDom
        : null}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`

const SelectBoxWrap = styled(HStack)<{ disabled?: boolean; $isOpen?: boolean; $size: Size }>`
  position: relative;
  width: 100%;
  background-color: ${({ disabled }) => (disabled ? colorSystem.bg.input.disabled : colors.white)};
  padding: 2px 8px;
  height: ${({ $size }) => ($size === 'sm' ? '24px' : '28px')};
  border: 1px solid ${({ $isOpen }) => ($isOpen ? colorSystem.border.element_active : colorSystem.border.element)};
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  color: ${colorSystem.text.common.primary};
  ${fontSystem.Body_14_regular};
`

const Value = styled(Text)`
  display: block;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`

const SelectBoxOptions = styled(VStack)<{ $size?: Size; $maxHeight: number }>`
  width: 100%;
  position: absolute;
  padding: 10px 8px;
  top: ${({ $size }) => ($size === 'sm' ? 24 : 32) + 4}px;
  right: 0;
  z-index: ${zIndexSystem.modal + 1};
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  overflow-y: scroll;
  border-radius: 4px;
  border: 1px solid ${colorSystem.border.element};
  background: ${colors.white};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
`

const Option = styled(Checkbox)<{ $border?: boolean }>`
  width: 100%;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  position: relative;

  &:hover {
    background: ${colorSystem.bg.common.secondary_hover};
  }

  ${({ $border }) =>
    $border &&
    css`
      &:after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: -4px;
        height: 1px;
        background-color: ${colorSystem.border.divider};
      }
    `}
`

export default React.forwardRef(MultipleSelectBox) as <T>(
  props: MultipleSelectBoxProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof MultipleSelectBox>
